import './schedule-health-professional-update-modal.scss';

import { useMutation } from '@apollo/client';
import {
  flatten,
  groupBy,
  isEmpty,
  kebabCase,
  mapValues,
  orderBy,
  uniq,
} from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { FormattedDate, useIntl } from 'react-intl';

import { useAppContext } from '../../../../data/context/app-context';
import { useHealthProfessionalItem } from '../../../../data/data/health-professionals/use-health-professional-item';
import {
  ScheduleConsultType,
  ScheduleDayPartType,
  ScheduleRangeType,
} from '../../../../data/data/schedule/enums';
import { useScheduleRangeTypeValues } from '../../../../data/data/schedule/select-values';
import { useScheduleDateRangeCheck } from '../../../../data/data/schedule/use-schedule-date-range-check';
import { useScheduleItems } from '../../../../data/data/schedule/use-schedule-items';
import {
  consultTypeToScheduleConsultType,
  getScheduleData,
  getScheduleDay,
  getScheduleDays,
} from '../../../../data/data/utils';
import {
  AgendaAppointmentItemDataFragment,
  ConsultType,
  GetAgendaItemsDocument,
  GetScheduleItemsDocument,
  TimeOfDay,
  UpdateScheduleItemDocument,
  WeekDay,
} from '../../../../data/types/api/graphql';
import { formatDate } from '../../../../data/utils/date.utils';
import { useFormatters } from '../../../../data/utils/formatters.utils';
import { AlertMessageType } from '../../../../elements/alert-message/alert-message';
import {
  Button,
  ButtonIcon,
  ButtonType,
} from '../../../../elements/button/button';
import { InputCheckboxController } from '../../../../elements/form/controllers/input-checkbox';
import { InputFieldController } from '../../../../elements/form/controllers/input-field';
import {
  FormColumns,
  FormRow,
  FormSection,
} from '../../../../elements/form/form';
import { BaseInputIcon } from '../../../../elements/form/input/base-input';
import { InputCheckboxType } from '../../../../elements/form/input/input-checkbox';
import { InputSwitch } from '../../../../elements/form/input/input-switch';
import { CustomFormattedDate } from '../../../../elements/intl/custom-formatted-date';
import { CustomFormattedMessage } from '../../../../elements/intl/custom-formatted-message';
import { Modal, ModalWidth } from '../../../../elements/modal/modal';
import { ModalPrompt } from '../../../../elements/modal/modal-prompt';
import { Tooltip } from '../../../../elements/tooltip/tooltip';
import { useScheduleUpdateForm } from './form/use-schedule-update-form';

type ScheduleHealthProfessionalUpdateModalProps = {
  healthProviderId: string | null | undefined;
  healthProfessionalId: string | null | undefined;
  onSaved?: () => void;
  onCancel?: () => void;
  onClose?: () => void;
};

export function ScheduleHealthProfessionalUpdateModal({
  healthProviderId,
  healthProfessionalId,
  onSaved,
  onCancel,
  onClose,
}: ScheduleHealthProfessionalUpdateModalProps) {
  const intl = useIntl();

  const { showAlertMessage } = useAppContext();

  const {
    data: healthProfessionalItemData,
    loading: healthProfessionalItemLoading,
  } = useHealthProfessionalItem(healthProfessionalId);

  const [formStep, setFormStep] = useState<number>(1);

  const onHandleFormStep = useCallback((nextStep: number) => {
    setFormStep(nextStep);
  }, []);

  const { form, formIsValid } = useScheduleUpdateForm();

  useEffect(() => {
    if (healthProfessionalItemData) {
      form.setValue('healthProfessionalId', healthProfessionalItemData.id, {
        shouldValidate: true,
      });
    }
  }, [healthProfessionalItemData, form]);

  const { data: rangeTypeData } = useScheduleRangeTypeValues();

  const rangeType = form.watch('rangeType');
  const startDate = form.watch('startDate');
  const endDate = form.watch('endDate');

  const { isMultiple, dateRange } = useMemo(
    () => ({
      isMultiple: rangeType !== ScheduleRangeType.SINGLE,
      dateRange:
        rangeType !== ScheduleRangeType.SINGLE
          ? { startDate, endDate }
          : { startDate, endDate: startDate },
    }),
    [rangeType, startDate, endDate]
  );

  const { data: scheduleDateRangeCheckData } = useScheduleDateRangeCheck({
    ...dateRange,
    healthProviderId,
    healthProfessionalId,
  });

  const defaultFirst = useMemo(() => {
    if (isMultiple && dateRange.startDate && dateRange.endDate) {
      const days =
        (new Date(dateRange.endDate).getTime() -
          new Date(dateRange.startDate).getTime()) /
        1000 /
        60 /
        60 /
        24;
      return days >= 8;
    }
    return false;
  }, [dateRange, isMultiple]);

  const { data: scheduleItemsData } = useScheduleItems(
    {
      healthProviderId,
      healthProfessionalId,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      defaultFirst,
    },
    true
  );

  const { scheduleDays, scheduleData } = useMemo(() => {
    const scheduleDays2 = getScheduleDays(
      startDate,
      isMultiple === true ? endDate : startDate
    );
    const allScheduledData = getScheduleData(startDate);

    return {
      scheduleDays: scheduleDays2,
      scheduleData: {
        ...allScheduledData,
        dates: allScheduledData.dates.filter(date =>
          scheduleDays2.includes(getScheduleDay(date)!)
        ),
      },
    };
  }, [startDate, endDate, isMultiple]);

  const [consultationHourDays, setConsultationHourDays] = useState<string[]>(
    []
  );

  const [consultationHourValues, setConsultationHourValues] = useState<{
    [key: string]: { [key in ScheduleDayPartType]: ScheduleConsultType[] };
  }>({});

  useEffect(() => {
    setConsultationHourDays(uniq(scheduleItemsData.map(option => option.day)));

    setConsultationHourValues(
      mapValues(
        mapValues(groupBy(scheduleItemsData, 'day'), option =>
          groupBy(option, 'timeOfDay')
        ),
        date =>
          mapValues(date, dayPart => {
            const values = uniq(
              flatten(
                dayPart.map(consult =>
                  consult.consultItem.consultTypes.map(consultType =>
                    consultTypeToScheduleConsultType(consultType)
                  )
                )
              )
            );

            return Object.values(ScheduleConsultType).filter(
              option => !values.includes(option)
            );
          })
      ) as {
        [key: string]: { [key in ScheduleDayPartType]: ScheduleConsultType[] };
      }
    );
  }, [scheduleItemsData, scheduleData]);

  const onHandleConsultationHourDate = useCallback((value: string) => {
    setConsultationHourDays(prevState => {
      const newState = [...prevState];

      const index = newState.findIndex(option => option === value);

      if (index === -1) {
        newState.push(value);
      } else {
        newState.splice(index, 1);
      }

      return newState;
    });
  }, []);

  const onHandleConsultationHourValue = useCallback(
    (day: string, dayPart: ScheduleDayPartType, value: ScheduleConsultType) => {
      setConsultationHourValues(prevState => {
        const currentDay = prevState[day] ?? {};
        const currentDayParts = [...(currentDay[dayPart] ?? [])];

        const index = currentDayParts.findIndex(option => option === value);

        if (index === -1) {
          currentDayParts.push(value);
        } else {
          currentDayParts.splice(index, 1);
        }

        return {
          ...prevState,
          [day]: {
            ...currentDay,
            [dayPart]: currentDayParts,
          },
        };
      });
    },
    []
  );

  const agendaDays = useMemo(
    () =>
      flatten(
        consultationHourDays.map(day =>
          flatten(
            scheduleData.dayParts.map(dayPart => {
              const currentDay = consultationHourValues[day] ?? {};
              const currentDayParts = currentDay[dayPart] ?? [];

              return {
                consults: Object.values(ScheduleConsultType)
                  .filter(consult => !currentDayParts.includes(consult))
                  .map(consult => consult as unknown as ConsultType),
                day: day as unknown as WeekDay,
                timeOfDay: dayPart as unknown as TimeOfDay,
              };
            })
          )
        )
      ),
    [consultationHourDays, consultationHourValues, scheduleData]
  );

  const getAppointments = useCallback(
    (
      date: string,
      dayPart: ScheduleDayPartType,
      value: {
        consultType: ScheduleConsultType;
      }
    ) => {
      const day = getScheduleDay(date)!;

      const currentAppointments =
        scheduleDateRangeCheckData.appointments[day] ?? {};

      return orderBy(
        (currentAppointments[dayPart] ?? []).filter(
          appointment =>
            consultTypeToScheduleConsultType(appointment.consultType) ===
            value.consultType
        ),
        'startTime'
      );
    },
    [scheduleDateRangeCheckData]
  );

  const getAppointmentsTooltip = useCallback(
    (appointments: AgendaAppointmentItemDataFragment[]) => {
      const getTime = (value: string | null | undefined) => {
        if (value) {
          return intl.formatDate(value, {
            hour: 'numeric',
            minute: 'numeric',
          });
        }

        return null;
      };

      const values = appointments
        .map(appointment => {
          if (appointment.startTime) {
            return `${intl.formatMessage(
              {
                id: 'common.appointment-date',
              },
              {
                date: intl.formatDate(appointment.startTime, {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                }),
                time: [
                  getTime(appointment.startTime),
                  getTime(appointment.endTime),
                ]
                  .filter(option => option)
                  .join(' - '),
              }
            )} : ${intl.formatMessage({
              id: `common.${kebabCase(appointment.consultType)}`,
            })}`;
          }

          return null;
        })
        .filter(appointment => appointment);

      return (
        <>
          {intl.formatMessage({ id: 'schedule.update.error.content' })}
          {values.map(appointment => (
            <span key={appointment}>{appointment}</span>
          ))}
        </>
      );
    },
    [intl]
  );

  const [updateScheduleItem, { loading: updateScheduleItemLoading }] =
    useMutation(UpdateScheduleItemDocument, {
      refetchQueries: [GetScheduleItemsDocument, GetAgendaItemsDocument],
    });

  const onHandleSubmit = useCallback(() => {
    form.handleSubmit(async () => {
      const values = form.getValues();

      try {
        const result = await updateScheduleItem({
          variables: {
            ...values,
            healthProviderId,
            agendaDays,
            startDate: values.startDate,
            endDate:
              values.rangeType === ScheduleRangeType.SINGLE
                ? values.startDate
                : values.endDate,
          },
        });

        if (result.data?.scheduleMutate.scheduleConsult?.success) {
          showAlertMessage({
            type: AlertMessageType.SUCCESS,
            message: intl.formatMessage({
              id: 'schedule.update.success',
            }),
          });

          onSaved?.();

          onClose?.();
        } else {
          const existingAppointments =
            result.data?.scheduleMutate.scheduleConsult?.existingAppointments ??
            [];

          if (existingAppointments.length > 0) {
            showAlertMessage({
              type: AlertMessageType.ERROR,
              message: getAppointmentsTooltip(existingAppointments),
            });
          } else {
            throw new Error(
              'An unknown error has occurred in the server respond.'
            );
          }
        }
      } catch (error) {
        showAlertMessage({
          type: AlertMessageType.ERROR,
          message: (error as Error).message,
        });
      }
    })();
  }, [
    intl,
    form,
    healthProviderId,
    agendaDays,
    updateScheduleItem,
    showAlertMessage,
    getAppointmentsTooltip,
    onSaved,
    onClose,
  ]);

  const [cancelPromptOpen, setCancelPromptOpen] = useState<boolean>(false);

  const onHandleCancel = useCallback(
    (confirmed: boolean = false) => {
      if (!confirmed && form.formState.isDirty) {
        setCancelPromptOpen(true);
      } else {
        onCancel?.();

        onClose?.();
      }
    },
    [form.formState.isDirty, onCancel, onClose]
  );

  const { getFullName } = useFormatters();

  const editMessage = useMemo(() => {
    if (isMultiple) {
      if (endDate) {
        return (
          <p>
            <CustomFormattedMessage
              id="common.schedule-update-description.case-2"
              values={{
                startDate: (
                  <CustomFormattedDate value={startDate} weekday="long" />
                ),
                endDate: <CustomFormattedDate value={endDate} weekday="long" />,
              }}
            />
          </p>
        );
      }
      return (
        <p>
          <CustomFormattedMessage
            id="common.schedule-update-description.case-3"
            values={{
              startDate: (
                <CustomFormattedDate value={startDate} weekday="long" />
              ),
            }}
          />
        </p>
      );
    }
    return (
      <p>
        <CustomFormattedMessage
          id="common.schedule-update-description.case-1"
          values={{
            date: <CustomFormattedDate value={startDate} weekday="long" />,
          }}
        />
      </p>
    );
  }, [endDate, isMultiple, startDate]);

  return (
    <>
      <Modal
        testId="update-schedule"
        loading={healthProfessionalItemLoading}
        title={
          <>
            <CustomFormattedMessage id="schedule.update.title" />
            {healthProfessionalItemData &&
              `: ${getFullName(healthProfessionalItemData)}`}
          </>
        }
        width={ModalWidth.LARGE}
        onClose={onHandleCancel}
        buttons={
          <>
            <Button
              type={ButtonType.SECONDARY}
              inverted
              onClick={() => onHandleCancel(false)}
            >
              <CustomFormattedMessage id="common.cancel" />
            </Button>

            {formStep > 1 && (
              <Button inverted onClick={() => onHandleFormStep(formStep - 1)}>
                <CustomFormattedMessage id="common.previous" />
              </Button>
            )}

            {formStep < 2 ? (
              <Button onClick={() => onHandleFormStep(formStep + 1)}>
                <CustomFormattedMessage id="common.next" />
              </Button>
            ) : (
              <Button
                icon={
                  updateScheduleItemLoading
                    ? ButtonIcon.LOADING
                    : ButtonIcon.NONE
                }
                disabled={!formIsValid || updateScheduleItemLoading}
                onClick={onHandleSubmit}
              >
                <CustomFormattedMessage id="schedule.update.save" />
              </Button>
            )}
          </>
        }
      >
        <FormProvider {...form}>
          {formStep === 1 && (
            <FormSection>
              <FormRow>
                <InputCheckboxController<ScheduleRangeType>
                  type={InputCheckboxType.RADIO}
                  name="rangeType"
                  label={
                    <>
                      <CustomFormattedMessage id="schedule.update.range-type" />
                      <small>*</small>
                    </>
                  }
                  options={rangeTypeData}
                />
              </FormRow>

              <hr />

              {!rangeType ? (
                <p className="info">
                  <CustomFormattedMessage id="schedule.update.schedule.select-range-type" />
                </p>
              ) : (
                <>
                  {rangeType === ScheduleRangeType.SINGLE && (
                    <FormRow>
                      <InputFieldController
                        icon={BaseInputIcon.DATE}
                        type="date"
                        name="startDate"
                        label={
                          <>
                            <CustomFormattedMessage id="common.date" />
                            <small>*</small>
                          </>
                        }
                        min={formatDate(new Date())}
                      />
                    </FormRow>
                  )}

                  {rangeType === ScheduleRangeType.MULTIPLE && (
                    <FormColumns>
                      <FormRow>
                        <InputFieldController
                          icon={BaseInputIcon.DATE}
                          type="date"
                          name="startDate"
                          label={
                            <>
                              <CustomFormattedMessage id="common.start-date" />
                              <small>*</small>
                            </>
                          }
                          min={formatDate(new Date())}
                          max={formatDate(
                            new Date(
                              new Date().setFullYear(
                                new Date().getFullYear() + 10
                              )
                            )
                          )}
                        />
                      </FormRow>

                      <FormRow>
                        <InputFieldController
                          icon={BaseInputIcon.DATE}
                          type="date"
                          name="endDate"
                          label={
                            <>
                              <CustomFormattedMessage id="common.end-date" />
                              <small>*</small>
                            </>
                          }
                          min={formatDate(new Date(startDate ?? ''))}
                        />
                      </FormRow>
                    </FormColumns>
                  )}
                </>
              )}
            </FormSection>
          )}

          {formStep === 2 &&
            (isEmpty(scheduleData.dates) ? (
              <FormSection>
                <p className="info">
                  <CustomFormattedMessage id="schedule.update.schedule.select-range-type" />
                </p>
              </FormSection>
            ) : (
              <>
                <h4>
                  <CustomFormattedMessage id="schedule.update.description" />
                </h4>

                {editMessage}

                <div className="week-schedule">
                  <div className="week-schedule__content">
                    <div className="week-schedule-table">
                      <div className="week-schedule-table__header">
                        <div className="week-schedule-table-item">
                          <div className="week-schedule-table-item__column week-schedule-table-item__column--label" />

                          {scheduleData.dates
                            .filter(date =>
                              scheduleDays.includes(getScheduleDay(date)!)
                            )
                            .map(date => (
                              <div
                                key={date}
                                className={`week-schedule-table-item__column${
                                  !scheduleDays.includes(getScheduleDay(date)!)
                                    ? ' inactive'
                                    : ''
                                }`}
                              >
                                <p>
                                  <FormattedDate value={date} weekday="short" />
                                </p>
                              </div>
                            ))}
                        </div>
                      </div>
                      <div className="week-schedule-table__content">
                        {scheduleData.dayParts.map(dayPart => (
                          <div
                            key={dayPart}
                            className="week-schedule-table-item"
                          >
                            <div className="week-schedule-table-item__column week-schedule-table-item__column--label">
                              <p>
                                <CustomFormattedMessage
                                  id={`common.${kebabCase(dayPart)}`}
                                />
                              </p>
                            </div>

                            {scheduleData.dates.map(date => {
                              const day = getScheduleDay(date)!;

                              const currentDay =
                                consultationHourValues[day] ?? {};
                              const currentDayParts = currentDay[dayPart] ?? [];

                              return (
                                <div
                                  key={day}
                                  className={`week-schedule-table-item__column${
                                    !scheduleDays.includes(day)
                                      ? ' inactive'
                                      : ''
                                  }`}
                                >
                                  {consultationHourDays.includes(day) &&
                                    scheduleDays.includes(day) && (
                                      <ul>
                                        {Object.values(ScheduleConsultType).map(
                                          option => {
                                            const appointments =
                                              getAppointments(date, dayPart, {
                                                consultType: option,
                                              }).filter(
                                                x =>
                                                  x.consultType !==
                                                    ConsultType.Else &&
                                                  x.consultType !==
                                                    ConsultType.Exception
                                              );

                                            if (appointments.length > 0) {
                                              return (
                                                <li key={option}>
                                                  <Tooltip
                                                    message={getAppointmentsTooltip(
                                                      appointments
                                                    )}
                                                  >
                                                    <i
                                                      className={`icon ${kebabCase(
                                                        option
                                                      )}${
                                                        !currentDayParts.includes(
                                                          option
                                                        )
                                                          ? ' active'
                                                          : ''
                                                      }`}
                                                    />
                                                  </Tooltip>
                                                </li>
                                              );
                                            }

                                            return (
                                              <li key={option}>
                                                <button
                                                  type="button"
                                                  className={
                                                    !currentDayParts.includes(
                                                      option
                                                    )
                                                      ? 'active'
                                                      : ''
                                                  }
                                                  onClick={() =>
                                                    onHandleConsultationHourValue(
                                                      day,
                                                      dayPart,
                                                      option
                                                    )
                                                  }
                                                >
                                                  <i
                                                    className={`icon ${kebabCase(
                                                      option
                                                    )}`}
                                                  />
                                                </button>
                                              </li>
                                            );
                                          }
                                        )}
                                      </ul>
                                    )}
                                </div>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                      <div className="week-schedule-table__footer">
                        <div className="week-schedule-table-item">
                          <div className="week-schedule-table-item__column week-schedule-table-item__column--label">
                            <p>
                              <CustomFormattedMessage id="common.consultation-hour" />
                            </p>
                          </div>

                          {scheduleData.dates.map(date => {
                            const day = getScheduleDay(date)!;

                            const currentDayAppointments =
                              scheduleDateRangeCheckData.appointments[day] ??
                              {};

                            return (
                              <div
                                key={date}
                                className={`week-schedule-table-item__column${
                                  !scheduleDays.includes(day) ? ' inactive' : ''
                                }`}
                              >
                                <InputSwitch
                                  name="notAvailable"
                                  value={
                                    scheduleDays.includes(day) &&
                                    consultationHourDays.includes(day)
                                  }
                                  disabled={
                                    !scheduleDays.includes(day) ||
                                    !isEmpty(currentDayAppointments)
                                  }
                                  onChange={() =>
                                    onHandleConsultationHourDate(day)
                                  }
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ))}
        </FormProvider>
      </Modal>

      {cancelPromptOpen && (
        <ModalPrompt
          onConfirm={() => onHandleCancel(true)}
          onClose={() => setCancelPromptOpen(false)}
        >
          <p>
            <CustomFormattedMessage id="common.confirm-not-saved-changes" />
          </p>
        </ModalPrompt>
      )}
    </>
  );
}
